import { Injectable } from '@angular/core';
import { LogoutService } from './logout.service';
import { LocalStorageService } from './storage/local-storage.service';

@Injectable()
export class CheckActivityService {
  private readonly LAST_ACTIVE_TIME_KEY = 'last_active_time';
  private readonly MINUTES_UNTIL_AUTO_LOGOUT_KEY = 'MINUTES_UNITL_AUTO_LOGOUT';
  private readonly MINUTES_BEFORE_PRE_LOGOUT_ALERT_KEY = 'minutes_before_pre_logout_alert';
  private readonly ENABLE_PRE_LOGOUT_ALERT_KEY = 'enable_pre_logout_alert';
  private readonly ACTIVITY_UPDATE_INTERVAL = 10000; // 10 seconds in milliseconds
  private readonly CHECK_INTERVAL = 30000; // Check every 30 seconds for better responsiveness

  private preLogoutAlertShown: boolean = false;
  private activityListenersSetup: boolean = false;
  private lastActivityUpdateTime: number = 0;
  private checkIntervalId: any = null;
  private loginCheckIntervalId: any = null;
  private storageEventListener: any = null;
  private onlineEventListener: any = null;
  private offlineEventListener: any = null;

  // Bound event handlers for cleanup
  private boundHandleMouseActivity: any = null;
  private boundHandleKeyboardActivity: any = null;
  private boundHandleScrollActivity: any = null;
  private boundHandleTouchActivity: any = null;
  private boundHandleVisibilityChange: any = null;

  constructor(
    private logoutService: LogoutService,
    private localStorageService: LocalStorageService
  ) {
    // Initialize after constructor to ensure all methods are available
    setTimeout(() => {
      this.setupStorageMonitoring();
      // Only initialize if user is logged in
      if (this.isUserLoggedIn()) {
        this.initializeActivityTracking();
      }
    }, 0);
  }

  /**
   * Check if user is logged in
   */
  private isUserLoggedIn(): boolean {
    try {
      // Check for user_access_token or user_data in localStorage
      const userAccessToken = localStorage.getItem('user_access_token');
      const userData = localStorage.getItem('user_data');
      const userId = this.localStorageService.getUserId();
      
      const isLoggedIn = (userAccessToken && userAccessToken !== 'null' && userAccessToken !== '') ||
                         (userData && userData !== 'null' && userData !== '') ||
                         (userId !== undefined && userId !== null && userId !== '');
      
      return isLoggedIn;
    } catch (error) {
      console.error('[CheckActivity] Error checking login status:', error);
      return false;
    }
  }

  /**
   * Setup storage event listener to detect logout
   */
  private setupStorageMonitoring(): void {
    this.storageEventListener = (event: StorageEvent) => {
      // Check if user data or access token was removed (logout detected)
      if (event.key === 'user_access_token' || event.key === 'user_data' || event.key === null) {
        // If key is null, it means localStorage was cleared
        if (event.key === null || !event.newValue || event.newValue === 'null' || event.newValue === '') {
          this.stopActivityTracking();
        }
      }
      
      // Check if user logged in (user_data or access_token was added)
      if ((event.key === 'user_access_token' || event.key === 'user_data') && event.newValue && event.newValue !== 'null' && event.newValue !== '') {
        if (!this.activityListenersSetup) {
          this.initializeActivityTracking();
        }
      }
    };
    
    window.addEventListener('storage', this.storageEventListener);
    
    // Setup online/offline event listeners
    this.onlineEventListener = () => {
      // User came back online - immediately check if they should be logged out
      if (this.isUserLoggedIn()) {
        this.checkInactivity();
      }
    };
    
    this.offlineEventListener = () => {
      // User went offline - don't logout, but continue tracking
      console.log('[CheckActivity] User went offline - will check for logout when back online');
    };
    
    window.addEventListener('online', this.onlineEventListener);
    window.addEventListener('offline', this.offlineEventListener);
    
    // Also check periodically for logout (in case storage event doesn't fire in same tab)
    if (this.loginCheckIntervalId) {
      clearInterval(this.loginCheckIntervalId);
    }
    this.loginCheckIntervalId = setInterval(() => {
      if (this.activityListenersSetup && !this.isUserLoggedIn()) {
        this.stopActivityTracking();
      } else if (!this.activityListenersSetup && this.isUserLoggedIn()) {
        this.initializeActivityTracking();
      }
    }, 5000); // Check every 5 seconds
  }

  /**
   * Initialize activity tracking and start checking for inactivity
   */
  private initializeActivityTracking(): void {
    // Only initialize if user is logged in
    if (!this.isUserLoggedIn()) {
      return;
    }
    
    if (this.activityListenersSetup) {
      return;
    }

    // Store bound handlers for cleanup
    this.boundHandleMouseActivity = this.handleActivity.bind(this);
    this.boundHandleKeyboardActivity = this.handleActivity.bind(this);
    this.boundHandleScrollActivity = this.handleActivity.bind(this);
    this.boundHandleTouchActivity = this.handleActivity.bind(this);
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);

    // Listen for mouse events
    document.addEventListener('mousemove', this.boundHandleMouseActivity);
    document.addEventListener('mousedown', this.boundHandleMouseActivity);
    document.addEventListener('mouseup', this.boundHandleMouseActivity);
    document.addEventListener('click', this.boundHandleMouseActivity);

    // Listen for keyboard events
    document.addEventListener('keydown', this.boundHandleKeyboardActivity);
    document.addEventListener('keyup', this.boundHandleKeyboardActivity);
    document.addEventListener('keypress', this.boundHandleKeyboardActivity);

    // Listen for scroll events
    document.addEventListener('scroll', this.boundHandleScrollActivity, true);
    window.addEventListener('scroll', this.boundHandleScrollActivity, true);

    // Listen for touch events (for mobile devices)
    document.addEventListener('touchstart', this.boundHandleTouchActivity);
    document.addEventListener('touchmove', this.boundHandleTouchActivity);
    document.addEventListener('touchend', this.boundHandleTouchActivity);

    // Listen for visibility change (when tab becomes visible)
    document.addEventListener('visibilitychange', this.boundHandleVisibilityChange);

    // Initialize last active time
    this.updateLastActiveTime();

    // Start periodic check for inactivity
    this.startInactivityCheck();

    // Do an immediate check after a short delay to ensure everything is set up
    setTimeout(() => {
      this.checkInactivity();
    }, 5000);

    this.activityListenersSetup = true;
  }

  /**
   * Handle any user activity event
   * This resets the inactivity timer by updating last_active_time
   */
  private handleActivity(event: Event): void {
    // Only track if user is logged in
    if (!this.isUserLoggedIn()) {
      return;
    }
    
    const wasUpdated = this.updateLastActiveTime();
    if (wasUpdated) {
    }
  }

  /**
   * Handle visibility change (tab switch, minimize, etc.)
   */
  private handleVisibilityChange(): void {
    // When tab becomes visible, update last active time
    if (!document.hidden) {
       this.updateLastActiveTime();
     
    }
  }

  /**
   * Update last active time in localStorage (throttled to 10 seconds)
   * Stores raw date and time as ISO string
   * Returns true if the time was actually updated, false if it was throttled
   */
  private updateLastActiveTime(): boolean {
    // Only update if user is logged in
    if (!this.isUserLoggedIn()) {
      return false;
    }
    
    try {
      const now = Date.now();
      
      // Only update if 10 seconds have passed since last update
      if (now - this.lastActivityUpdateTime >= this.ACTIVITY_UPDATE_INTERVAL) {
        // Store raw date and time as ISO string
        const currentDateTime = new Date().toISOString();
        localStorage.setItem(this.LAST_ACTIVE_TIME_KEY, currentDateTime);
        this.lastActivityUpdateTime = now;
        return true;
      }
      // Update was throttled (less than 10 seconds since last update)
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get last active time from localStorage
   */
  public getLastActiveTime(): string | null {
    try {
      return localStorage.getItem(this.LAST_ACTIVE_TIME_KEY);
    } catch (error) {
      console.error('Error getting last active time:', error);
      return null;
    }
  }

  private getLsNum(key: string, def: number): number {
    const n = parseFloat(localStorage.getItem(key));
    return isNaN(n) ? def : n;
  }

  private isPreLogoutOn(): boolean {
    return localStorage.getItem(this.ENABLE_PRE_LOGOUT_ALERT_KEY) === '1';
  }

  private clearPreLogoutPopup(): void {
    const el = document.getElementById('pre-logout-alert-overlay');
    if (el) {
      el.remove();
    }
    this.preLogoutAlertShown = false;
  }

  private showPreLogoutPopup(mins: number): void {
    this.clearPreLogoutPopup();
    const overlay = document.createElement('div');
    overlay.id = 'pre-logout-alert-overlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:99999;display:flex;align-items:center;justify-content:center';
    const box = document.createElement('div');
    box.style.cssText = 'background:#fff;padding:24px 28px;border-radius:8px;max-width:420px;text-align:center;cursor:pointer';
    box.innerHTML = '<p style="margin:0 0 12px;font-weight:600">You will be automatically logged out in ' + mins +
      ' minute(s).</p><p style="margin:0 0 16px;font-size:13px;color:#666">Click OK to stay logged in.</p>' +
      '<button style="padding:8px 24px;background:#035168;color:#fff;border:none;border-radius:4px;cursor:pointer">OK</button>';
    const self = this;
    box.onclick = function () {
      self.clearPreLogoutPopup();
      self.resetTimerOnPreLogoutOk();
    };
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    this.preLogoutAlertShown = true;
  }

  /** Reset timer when user clicks OK on pre-logout popup (new logic only) */
  private resetTimerOnPreLogoutOk(): void {
    if (!this.isUserLoggedIn()) {
      return;
    }
    const now = Date.now();
    localStorage.setItem(this.LAST_ACTIVE_TIME_KEY, new Date().toISOString());
    this.lastActivityUpdateTime = now;
  }

  /** Pre-logout popup — runs only when enable_pre_logout_alert is '1' */
  private handlePreLogoutAlert(inactiveMinutes: number, logoutMinutes: number): void {
    if (!this.isPreLogoutOn()) {
      if (this.preLogoutAlertShown) {
        this.clearPreLogoutPopup();
      }
      return;
    }
    const beforeMins = this.getLsNum(this.MINUTES_BEFORE_PRE_LOGOUT_ALERT_KEY, 1);
    const alertAt = logoutMinutes - beforeMins;
    if (beforeMins > 0 && beforeMins < logoutMinutes && inactiveMinutes >= alertAt &&
      inactiveMinutes < logoutMinutes && !this.preLogoutAlertShown) {
      this.showPreLogoutPopup(beforeMins);
    }
  }

  /**
   * Get minutes until auto logout from localStorage
   */
  private getMinutesUntilAutoLogout(): number {
    try {
      const minutes = localStorage.getItem(this.MINUTES_UNTIL_AUTO_LOGOUT_KEY);
      if (minutes) {
        const parsedMinutes = parseFloat(minutes);
        return isNaN(parsedMinutes) ? 10 : parsedMinutes; // Default to 10 minutes if invalid
      }
      return 10; // Default to 10 minutes if not set
    } catch (error) {
      console.error('Error getting minutes until auto logout:', error);
      return 10; // Default to 10 minutes on error
    }
  }

  /**
   * Check if user has been inactive for the specified duration
   */
  private checkInactivity(): void {
    // Only check if user is logged in
    if (!this.isUserLoggedIn()) {
      // User is not logged in, stop tracking
      this.stopActivityTracking();
      return;
    }
    
    try {
      const lastActiveTimeStr = this.getLastActiveTime();
      if (!lastActiveTimeStr) {
        // No activity recorded, initialize it
        this.updateLastActiveTime();
        return;
      }

      const lastActiveTime = new Date(lastActiveTimeStr);
      const now = new Date();
      
      // Validate the date
      if (isNaN(lastActiveTime.getTime())) {
        this.updateLastActiveTime();
        return;
      }

      const inactiveDurationMs = now.getTime() - lastActiveTime.getTime();
      const inactiveDurationMinutes = inactiveDurationMs / (1000 * 60);

      const minutesUntilLogout = this.getMinutesUntilAutoLogout();

      // NEW: pre-logout alert (does not change existing logout below)
      this.handlePreLogoutAlert(inactiveDurationMinutes, minutesUntilLogout);

      // If user has been continuously inactive for the specified duration
      if (inactiveDurationMinutes >= minutesUntilLogout) {
        this.clearPreLogoutPopup();
        console.warn(`[CheckActivity] ⚠️ User continuously inactive for ${inactiveDurationMinutes.toFixed(2)} minutes (threshold: ${minutesUntilLogout} minutes).`);
        
        // Only logout if user is online
        // If user is offline, wait until they come back online (handled by online event listener)
        if (navigator.onLine) {
          console.warn(`[CheckActivity] User is online - triggering logout...`);
          this.logoutService.logout([], 'force');
        } else {
          console.warn(`[CheckActivity] User is offline - will logout when connection is restored`);
        }
      } 
    } catch (error) {
      console.error('[CheckActivity] Error checking inactivity:', error);
    }
  }

  /**
   * Start periodic check for inactivity
   */
  private startInactivityCheck(): void {
    // Clear any existing interval
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
    }

    
    // Check for inactivity periodically
    this.checkIntervalId = setInterval(() => {
      this.checkInactivity();
    }, this.CHECK_INTERVAL);
  }

  /**
   * Stop inactivity check
   */
  private stopInactivityCheck(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  /**
   * Stop all activity tracking (called on logout)
   */
  private stopActivityTracking(): void {
    this.clearPreLogoutPopup();
    this.stopInactivityCheck();
    this.removeActivityListeners();
    this.clearLastActiveTime();
  }

  /**
   * Remove activity tracking listeners
   */
  private removeActivityListeners(): void {
    if (!this.activityListenersSetup) {
      return;
    }

    try {
      if (this.boundHandleMouseActivity) {
        document.removeEventListener('mousemove', this.boundHandleMouseActivity);
        document.removeEventListener('mousedown', this.boundHandleMouseActivity);
        document.removeEventListener('mouseup', this.boundHandleMouseActivity);
        document.removeEventListener('click', this.boundHandleMouseActivity);
      }

      if (this.boundHandleKeyboardActivity) {
        document.removeEventListener('keydown', this.boundHandleKeyboardActivity);
        document.removeEventListener('keyup', this.boundHandleKeyboardActivity);
        document.removeEventListener('keypress', this.boundHandleKeyboardActivity);
      }

      if (this.boundHandleScrollActivity) {
        document.removeEventListener('scroll', this.boundHandleScrollActivity, true);
        window.removeEventListener('scroll', this.boundHandleScrollActivity, true);
      }

      if (this.boundHandleTouchActivity) {
        document.removeEventListener('touchstart', this.boundHandleTouchActivity);
        document.removeEventListener('touchmove', this.boundHandleTouchActivity);
        document.removeEventListener('touchend', this.boundHandleTouchActivity);
      }

      if (this.boundHandleVisibilityChange) {
        document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);
      }

      this.activityListenersSetup = false;
    } catch (error) {
      console.error('Error removing activity listeners:', error);
    }
  }

  /**
   * Clear last active time from localStorage
   */
  public clearLastActiveTime(): void {
    try {
      localStorage.removeItem(this.LAST_ACTIVE_TIME_KEY);
      this.lastActivityUpdateTime = 0;
    } catch (error) {
      console.error('Error clearing last active time:', error);
    }
  }

  /**
   * Cleanup method - remove all listeners and clear intervals
   */
  public cleanup(): void {
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
      this.storageEventListener = null;
    }
    if (this.onlineEventListener) {
      window.removeEventListener('online', this.onlineEventListener);
      this.onlineEventListener = null;
    }
    if (this.offlineEventListener) {
      window.removeEventListener('offline', this.offlineEventListener);
      this.offlineEventListener = null;
    }
    if (this.loginCheckIntervalId) {
      clearInterval(this.loginCheckIntervalId);
      this.loginCheckIntervalId = null;
    }
    this.stopActivityTracking();
  }

  /**
   * Manually trigger activity update (useful for external triggers)
   */
  public triggerActivityUpdate(): void {
    this.updateLastActiveTime();
  }

  /**
   * Manually check inactivity (useful for external triggers)
   */
  public manualInactivityCheck(): void {
    this.checkInactivity();
  }
}




// WEBPACK FOOTER //
// /codebuild/output/src1639192363/src/src/app/shared/services/check-activity.service.ts